import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const isAuthError = this.state.error?.message?.includes('Invalid token') || 
                         this.state.error?.message?.includes('401') ||
                         this.state.error?.message?.includes('Unauthorized');

      return (
        <Container maxWidth="sm">
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h4" color="error" gutterBottom>
                {isAuthError ? 'Authentication Error' : 'Something went wrong'}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                {isAuthError 
                  ? 'There was an authentication issue. This usually happens when your session expires.'
                  : 'An unexpected error occurred. Please try refreshing the page.'
                }
              </Typography>

              {isAuthError && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Error: {this.state.error?.message}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                {isAuthError && (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={this.handleLogout}
                  >
                    Clear Session & Reload
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={this.handleReload}
                >
                  Refresh Page
                </Button>
              </Box>
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 